// Serviço para gerenciar vínculos entre usuários e empresas
import { supabase } from '../lib/supabase';

// Interface para vínculo usuário-empresa
export interface UsuarioEmpresa {
  id: string;
  usuarioId: string;
  empresaId: string;
  role: 'admin' | 'operador' | 'visualizador';
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

// Interface para vínculo no banco de dados
interface UsuarioEmpresaDB {
  id: string;
  usuario_id: string;
  empresa_id: string;
  role: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

// Converter do banco para aplicação
function usuarioEmpresaDBToUsuarioEmpresa(ueDB: UsuarioEmpresaDB): UsuarioEmpresa {
  return {
    id: ueDB.id,
    usuarioId: ueDB.usuario_id,
    empresaId: ueDB.empresa_id,
    role: ueDB.role as 'admin' | 'operador' | 'visualizador',
    ativo: ueDB.ativo,
    createdAt: ueDB.created_at,
    updatedAt: ueDB.updated_at,
  };
}

// Buscar todos os usuários de uma empresa
export async function getUsuariosEmpresa(empresaId: string): Promise<UsuarioEmpresa[]> {
  try {
    const { data: usuarios, error } = await supabase
      .from('usuarios_empresas')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('ativo', true)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (usuarios || []).map(usuarioEmpresaDBToUsuarioEmpresa);
  } catch (error) {
    console.error('Erro ao buscar usuários da empresa:', error);
    throw error;
  }
}

// Buscar empresas vinculadas ao usuário autenticado
export async function getEmpresasUsuario(usuarioId?: string): Promise<UsuarioEmpresa[]> {
  try {
    // Se não fornecido, usar o usuário autenticado
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error('[usuariosService] Erro ao obter usuário:', userError);
      throw new Error('Erro ao obter usuário autenticado');
    }
    
    const userId = usuarioId || user?.id;

    if (!userId) {
      throw new Error('Usuário não autenticado');
    }

    console.log('[usuariosService] Buscando empresas para usuário:', userId);

    const { data: empresas, error } = await supabase
      .from('usuarios_empresas')
      .select('*')
      .eq('usuario_id', userId)
      .eq('ativo', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[usuariosService] Erro na query:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
      });
      throw error;
    }

    console.log('[usuariosService] Empresas encontradas:', empresas?.length || 0);
    return (empresas || []).map(usuarioEmpresaDBToUsuarioEmpresa);
  } catch (error: any) {
    console.error('[usuariosService] Erro ao buscar empresas do usuário:', error);
    // Se for erro de tabela não encontrada, retornar array vazio
    if (error?.code === '42P01' || error?.message?.includes('does not exist')) {
      console.warn('[usuariosService] Tabela usuarios_empresas não existe, retornando array vazio');
      return [];
    }
    throw error;
  }
}

// Vincular usuário a uma empresa
export async function vincularUsuarioEmpresa(
  usuarioId: string,
  empresaId: string,
  role: 'admin' | 'operador' | 'visualizador' = 'visualizador'
): Promise<UsuarioEmpresa> {
  try {
    // Tentar inserir com select primeiro (pode funcionar se RLS permitir)
    let { data: vinculo, error: insertError } = await supabase
      .from('usuarios_empresas')
      .insert({
        usuario_id: usuarioId,
        empresa_id: empresaId,
        role,
        ativo: true,
      })
      .select()
      .single();
    
    // Se deu erro de recursão, tentar sem select
    if (insertError && insertError.message?.includes('recursion')) {
      console.log('[usuariosService] Erro de recursão detectado, inserindo sem select...');
      
      // Inserir sem select
      const { error: insertError2 } = await supabase
        .from('usuarios_empresas')
        .insert({
          usuario_id: usuarioId,
          empresa_id: empresaId,
          role,
          ativo: true,
        });
      
      if (insertError2) {
        throw insertError2;
      }
      
      // Se inseriu sem erro, criar objeto de retorno manualmente
      // Não buscar do banco para evitar recursão
      const vinculoManual: UsuarioEmpresa = {
        id: `temp-${Date.now()}`, // ID temporário, não usado para nada crítico
        usuarioId,
        empresaId,
        role,
        ativo: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      return vinculoManual;
    }
    
    // Se deu outro erro, lançar
    if (insertError) {
      throw insertError;
    }
    
    // Se inseriu com sucesso e retornou dados, converter
    if (vinculo) {
      return usuarioEmpresaDBToUsuarioEmpresa(vinculo);
    }
    
    // Se inseriu mas não retornou dados, criar manualmente
    const vinculoManual: UsuarioEmpresa = {
      id: `temp-${Date.now()}`,
      usuarioId,
      empresaId,
      role,
      ativo: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    return vinculoManual;
  } catch (error) {
    console.error('[usuariosService] Erro ao vincular usuário à empresa:', error);
    throw error;
  }
}

// Atualizar role do usuário na empresa
export async function atualizarRoleUsuario(
  id: string,
  role: 'admin' | 'operador' | 'visualizador'
): Promise<UsuarioEmpresa> {
  try {
    const { data: vinculo, error } = await supabase
      .from('usuarios_empresas')
      .update({ role })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return usuarioEmpresaDBToUsuarioEmpresa(vinculo);
  } catch (error) {
    console.error('Erro ao atualizar role do usuário:', error);
    throw error;
  }
}

// Remover vínculo usuário-empresa (desativar)
export async function removerVinculoUsuario(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('usuarios_empresas')
      .update({ ativo: false })
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Erro ao remover vínculo:', error);
    throw error;
  }
}

// Obter role do usuário na empresa atual
export async function getRoleUsuarioEmpresa(empresaId: string): Promise<'admin' | 'operador' | 'visualizador' | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) return null;

    const { data: vinculo, error } = await supabase
      .from('usuarios_empresas')
      .select('role')
      .eq('usuario_id', user.id)
      .eq('empresa_id', empresaId)
      .eq('ativo', true)
      .single();

    if (error) return null;

    return vinculo?.role as 'admin' | 'operador' | 'visualizador' || null;
  } catch (error) {
    console.error('Erro ao obter role do usuário:', error);
    return null;
  }
}

// Desvincular usuário de uma empresa específica
export async function desvincularUsuarioEmpresa(
  usuarioId: string,
  empresaId: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('usuarios_empresas')
      .update({ ativo: false })
      .eq('usuario_id', usuarioId)
      .eq('empresa_id', empresaId);

    if (error) throw error;
  } catch (error) {
    console.error('[usuariosService] Erro ao desvincular usuário:', error);
    throw error;
  }
}

// Buscar usuários vinculados a uma empresa
export async function getUsuariosDaEmpresa(empresaId: string): Promise<UsuarioEmpresa[]> {
  try {
    const { data: usuarios, error } = await supabase
      .from('usuarios_empresas')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('ativo', true)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (usuarios || []).map(usuarioEmpresaDBToUsuarioEmpresa);
  } catch (error) {
    console.error('[usuariosService] Erro ao buscar usuários da empresa:', error);
    throw error;
  }
}

